import React from "react";

import ProductOverview from "../components/ProductOverview";
import { useProductDetailContext } from "../context/ProductDetailContext";

import AddSubProductItemModal from "./components/AddItemModal";
import SubProductItemLogSheet from "./components/SubProductItemLog";
import UpdateItemModal from "./components/UpdateItemModal";
import SubProductItemsTable from "./table/ItemTable";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import CustomHeader from "@/components/reusables/Headers.tsx";
import Search from "@/components/reusables/data-table/SearchFunction";
import * as Sheet from "@/components/ui/sheet";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { IProductLogsItem } from "@/interfaces/tools.interface";
import { useGetSubProductItemsQuery } from "@/redux/api/toolsQuery";

interface SubProductItemSheetProps {
  isOpen: boolean;
  toggleSubProductItemSheet: () => void;
}

const SubProductItemSheet = ({
  isOpen,
  toggleSubProductItemSheet,
}: SubProductItemSheetProps): JSX.Element => {
  const { productId } = useProductDetailContext();
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined,
  );
  const { id: subProductId, reset } = useQueryID(ToolsKeys.SUB_PRODUCT_ID);
  const [isShowAddItemModal, setIsShowAddItemModal] = React.useState(false);
  const [isShowEditItemModal, setIsShowEditItemModal] = React.useState(false);
  const [isShowItemLogSheet, setIsShowItemLogSheet] = React.useState<{
    isOpen: boolean;
    itemName: string;
    data: IProductLogsItem[];
  } | null>(null);

  const { data, ...rest } = useGetSubProductItemsQuery(
    {
      productId,
      subProductId,
    },
    {
      skip: !productId || !subProductId || !isOpen,
    },
  );

  const itemsData = data?.data || [];

  const handleExit = (): void => {
    reset();
    toggleSubProductItemSheet();
    localStorage.removeItem(ToolsKeys.SUB_PRODUCT_NAME);
  };
  const subProductName =
    localStorage.getItem(ToolsKeys.SUB_PRODUCT_NAME) || "N/A";

  return (
    <Sheet.Sheet open={isOpen} onOpenChange={handleExit}>
      <Sheet.SheetContent className='min-w-[80dvw] space-y-4'>
        <FetchWrapper {...rest}>
          <Sheet.SheetHeader>
            <CustomHeader
              title={`${subProductName} : ${ToolsConstants.PRODUCTS.ITEMS}`}
              backBtn={toggleSubProductItemSheet}
              custom={{
                position: "left",
                node: (
                  <Search
                    key={"search"}
                    search={"search"}
                    updateParams={(key, value) => {
                      if (key === "search") {
                        setSearchQuery(value as string);
                      }
                    }}
                  />
                ),
              }}
            />
          </Sheet.SheetHeader>

          <ProductOverview
            data={[
              {
                label: ToolsConstants.PRODUCTS.TOTAL_ITEMS,
                value:
                  Object.values(data?.statusCounts || {}).reduce(
                    (sum, value) => sum + value,
                    0,
                  ) || 0,
              },
              {
                label: ToolsConstants.PRODUCTS.AVAILABLE,
                value: data?.statusCounts?.available || 0,
              },
              {
                label: ToolsConstants.PRODUCTS.CONSUMNED,
                value: data?.statusCounts?.consumed || 0,
              },
              {
                label: ToolsConstants.PRODUCTS.UNDER_REPAIR,
                value: data?.statusCounts?.repair || 0,
              },
            ]}
          />
          <SubProductItemsTable
            data={itemsData?.filter(
              (data) =>
                !searchQuery ||
                data?.name
                  ?.toLowerCase()
                  .includes(searchQuery.trim().toLowerCase()),
            )}
            toggleAddSubProductItemModal={() => setIsShowAddItemModal(true)}
            toggleEditSubProductItemModal={() => setIsShowEditItemModal(true)}
            toggleViewLogItemSheet={setIsShowItemLogSheet}
          />
        </FetchWrapper>
        {isShowAddItemModal && (
          <AddSubProductItemModal
            isOpen={isShowAddItemModal}
            toggleAddSubProductItemModal={(): void =>
              setIsShowAddItemModal(!isShowAddItemModal)
            }
          />
        )}
        {isShowEditItemModal && (
          <UpdateItemModal
            isOpen={isShowEditItemModal}
            toggleUpdateItemModal={(): void =>
              setIsShowEditItemModal(!isShowEditItemModal)
            }
          />
        )}
        {isShowItemLogSheet?.isOpen && (
          <SubProductItemLogSheet
            data={isShowItemLogSheet.data}
            itemName={isShowItemLogSheet.itemName}
            isOpen={isShowItemLogSheet.isOpen}
            toggleSubProductItemLogSheet={() => setIsShowItemLogSheet(null)}
          />
        )}
      </Sheet.SheetContent>
    </Sheet.Sheet>
  );
};

export default SubProductItemSheet;
